import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CollapsedContent from 'components/uiLibrary/CollapsedContent';

// eslint-disable-next-line import/no-cycle
import Login from 'components/Globals/Accounts/Login';
import useTracking from 'components/Globals/Analytics';
import {
  COMPONENTS,
  GOOGLE_OLD_TRACKING_SERVICES,
  SECTIONS,
  SUB_COMPONENTS,
} from 'components/Globals/Analytics/constants';

import { LOGIN_SOURCE, PAYWALL_STEPS, STEP_VALUES, STEPS_LOGIN } from 'constants/index';
import { SUBSCRIPTION_GROUP_IDENTIFIERS } from 'constants/subscriptions';

import useAppContext from 'utils/hooks/useAppContext';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePurchaseSubscription from 'utils/hooks/usePurchaseSubscription';
import { useStripeCheckout } from 'utils/hooks/useStripeCheckout';
import { PAYWALL_EVENT_IDS, TRACK_EVENTS } from 'utils/tracking';

import { ENTITY_TYPES } from 'operabase-router/constants';

import PaywallDrawerWrapper from '../PaywallDrawerWrapper';
import PlanGroupListing from '../PlanGroupListing';
import PlanGroupView from '../PlanGroupView';
import StickyPaywallFooter from '../StickyPaywallFooter';

import classes from './PaywallContent.module.scss';

const PaywallDrawer = ({
  paywallStep,
  onBackHandler,
  onSelectPlan,
  selectedGroupIdentifier,
  setSelectedGroupIdentifier,
  showSubscriptionPlans,
  onCloseHandler,
  isRedirecting,
  processingProductIds,
}) => {
  const { paywall } = useAppContext();
  const { isLoginDialog, setIsLoginDialog } = useDialogs();
  const paywallType = paywall?.paywallType;

  useEffect(() => {
    const loginSource = isLoginDialog.source;
    const expectedLoginSource = paywallType || LOGIN_SOURCE.PAYWALL_SIDE_CLICK;

    if (
      paywallStep === PAYWALL_STEPS.LOGIN_DRAWER &&
      loginSource !== LOGIN_SOURCE.RETURNING_USER &&
      loginSource !== expectedLoginSource
    ) {
      setIsLoginDialog({ ...isLoginDialog, source: paywallType || LOGIN_SOURCE.PAYWALL_SIDE_CLICK });
    }
  }, [paywallStep, paywallType, isLoginDialog, setIsLoginDialog]);

  const initialLoginStep = useMemo(() => {
    if (paywallStep !== PAYWALL_STEPS.LOGIN_DRAWER) {
      return null;
    }

    if (isLoginDialog?.step) {
      return isLoginDialog.step;
    }

    if (isLoginDialog.source === LOGIN_SOURCE.MANDATORY_NAME) {
      return STEP_VALUES[STEPS_LOGIN.REGISTRATION_UPDATE];
    }

    return STEP_VALUES[STEPS_LOGIN.SELECT];
  }, [paywallStep, isLoginDialog]);

  return (
    <PaywallDrawerWrapper
      paywallStep={paywallStep}
      onClose={onCloseHandler}
      onBack={onBackHandler}
      allowBackTransition={!!selectedGroupIdentifier && !showSubscriptionPlans?.entityType}
      allowCloseButton={!!showSubscriptionPlans?.entityType}
    >
      {paywallStep === PAYWALL_STEPS.LOGIN_DRAWER && (
        <CollapsedContent maxHeightPx={500}>
          <div className={classes.loginContainer}>
            <Login step={initialLoginStep} isModal={false} />
          </div>
        </CollapsedContent>
      )}
      {paywallStep === PAYWALL_STEPS.PLAN_GROUP_LISTING && (
        <CollapsedContent maxHeightPx={500}>
          <div className={classes.planListingContainer}>
            <PlanGroupListing onChoose={setSelectedGroupIdentifier} />
          </div>
        </CollapsedContent>
      )}
      {paywallStep === PAYWALL_STEPS.PLAN_GROUP_DETAILS && (
        <CollapsedContent maxHeightPx={500}>
          <PlanGroupView
            {...showSubscriptionPlans}
            identifier={selectedGroupIdentifier}
            onSelectPlan={onSelectPlan}
            isRedirecting={isRedirecting}
            processingProductIds={processingProductIds}
          />
        </CollapsedContent>
      )}
    </PaywallDrawerWrapper>
  );
};

const PaywallContent = ({
  showSubscriptionPlans = {},
  setShowSubscriptionPlans,
  isPaywallExpanded,
  setIsPaywallExpanded,
}) => {
  const { isLoggedIn, paywall } = useAppContext();
  const paywallType = paywall?.paywallType;
  const { isLoginDialog } = useDialogs();
  const { isLoading, processingProductIds } = useStripeCheckout();
  const onChangeProduct = useCallback(
    ({ entityType, entity, subscription }) => {
      setShowSubscriptionPlans({
        show: true,
        entityType,
        entity,
        subscription,
      });
    },
    [setShowSubscriptionPlans],
  );

  const onPurchaseSubscription = usePurchaseSubscription({ onChangeProduct });

  const track = useTracking();
  const getGroupIdentifier = useCallback(entityType => {
    switch (entityType) {
      case ENTITY_TYPES.PROFILE:
        return SUBSCRIPTION_GROUP_IDENTIFIERS.ARTIST_PROFESSIONAL;
      case ENTITY_TYPES.ORGANIZATION:
      case ENTITY_TYPES.AGENCY:
        return SUBSCRIPTION_GROUP_IDENTIFIERS.ORG_PREMIUM;
      default:
        return null;
    }
  }, []);

  const [selectedGroupIdentifier, setSelectedGroupIdentifier] = useState(() => {
    if (!showSubscriptionPlans.show) {
      return null;
    }

    return getGroupIdentifier(showSubscriptionPlans.entityType);
  });

  useEffect(() => {
    if (showSubscriptionPlans.show) {
      setIsPaywallExpanded(true);
      setSelectedGroupIdentifier(
        showSubscriptionPlans?.identifier || getGroupIdentifier(showSubscriptionPlans.entityType),
      );
    }
  }, [showSubscriptionPlans, getGroupIdentifier, setIsPaywallExpanded]);

  useEffect(() => {
    if (isLoginDialog?.isOpen && isLoginDialog?.step) {
      setSelectedGroupIdentifier(null);
    }
  }, [isLoginDialog]);

  const paywallStep = useMemo(() => {
    if (isPaywallExpanded) {
      if (selectedGroupIdentifier) {
        return PAYWALL_STEPS.PLAN_GROUP_DETAILS;
      }

      if (isLoggedIn && !isLoginDialog?.isOpen) {
        return PAYWALL_STEPS.PLAN_GROUP_LISTING;
      }

      return PAYWALL_STEPS.LOGIN_DRAWER;
    }

    return PAYWALL_STEPS.STICKY_FOOTER;
  }, [isLoggedIn, isPaywallExpanded, selectedGroupIdentifier, isLoginDialog?.isOpen]);

  const onCloseHandler = useCallback(() => {
    setIsPaywallExpanded(false);
    setSelectedGroupIdentifier(null);
    setShowSubscriptionPlans({ show: false });

    if (paywallType) {
      track.closePaywall(paywallType);
      track.click(
        {
          name: TRACK_EVENTS.CLICK,
          data: {
            id: PAYWALL_EVENT_IDS.PAYWALL_IGNORE,
            actionType: paywallType,
          },
        },
        GOOGLE_OLD_TRACKING_SERVICES,
      );
    } else {
      track.click({
        section: SECTIONS.PAYWALL,
        component: COMPONENTS.EXPANDABLE_CARD,
        subComponent: SUB_COMPONENTS.SHOW_HIDE_TOGGLE,
        meta: {
          actionType: paywallType,
          isOpen: true,
        },
      });
    }
  }, [setIsPaywallExpanded, setSelectedGroupIdentifier, setShowSubscriptionPlans, paywallType, track]);

  const onBackHandler = useCallback(() => {
    if (selectedGroupIdentifier && !showSubscriptionPlans.identifier) {
      setSelectedGroupIdentifier(null);
    } else {
      onCloseHandler();
    }
  }, [selectedGroupIdentifier, showSubscriptionPlans.identifier, onCloseHandler]);

  const onSelectPlan = useCallback(
    plan => {
      if (plan?.productId) {
        onPurchaseSubscription({
          productId: plan?.productId,
          entityType: showSubscriptionPlans.entityType,
          entity: {
            ...showSubscriptionPlans.entity,
            subscription: showSubscriptionPlans.subscription,
          },
        });
      } else if (plan?.productGroup) {
        setSelectedGroupIdentifier(plan.productGroup);
      } else {
        setSelectedGroupIdentifier(null);
      }

      return null;
    },
    [
      showSubscriptionPlans.entityType,
      showSubscriptionPlans.entity,
      showSubscriptionPlans.subscription,
      onPurchaseSubscription,
    ],
  );

  if (paywallStep === PAYWALL_STEPS.STICKY_FOOTER) {
    return <StickyPaywallFooter onClick={() => setIsPaywallExpanded(true)} />;
  }

  return (
    <PaywallDrawer
      paywallStep={paywallStep}
      onBackHandler={onBackHandler}
      onSelectPlan={onSelectPlan}
      selectedGroupIdentifier={selectedGroupIdentifier}
      setSelectedGroupIdentifier={setSelectedGroupIdentifier}
      showSubscriptionPlans={showSubscriptionPlans}
      onCloseHandler={onCloseHandler}
      isRedirecting={isLoading}
      processingProductIds={processingProductIds}
    />
  );
};

export default PaywallContent;
